import React from 'react';
import PropTypes from 'prop-types';
import './ExternalLinkButton.css';

/**
 * ExternalLinkButton Component
 * A reusable button/link component for opening external URLs in a new tab.
 * Provides consistent styling and behavior across the application.
 * 
 * @param {Object} props
 * @param {string} props.href - The URL to open
 * @param {React.ReactNode} props.children - The content of the button (text, icons, etc.)
 * @param {string} [props.variant='primary'] - Visual variant: 'primary' (larger) or 'secondary' (smaller)
 * @param {string} [props.title] - Optional tooltip text
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Optional click handler
 */
const ExternalLinkButton = ({ 
  href, 
  children, 
  variant = 'primary',
  title,
  className = '',
  onClick,
  ...restProps 
}) => {
  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const combinedClassName = `external-link-btn external-link-btn--${variant} ${className}`.trim();

  return (
    <a
      className={combinedClassName}
      href={href}
      onClick={handleClick}
      rel="noopener noreferrer"
      target="_blank"
      title={title}
      {...restProps}
    >
      {children}
    </a>
  );
};

ExternalLinkButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  href: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  title: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary']),
};

export default ExternalLinkButton;
